var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PredictorConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { BaseMachineLearningConfig } from './baseMachineLearningConfig';
import { ToolCategories } from './toolCategories';
import { ToolConfigInfo } from './toolConfigInfo';
import { getRenamedColumns } from './toolConfigUtil';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid, validateSourceWithDetail } from './validation/validateHelper';
import { predictorConfigSchemaValidation } from '../validations.generated';
export const MIN_OPTIONS = 1;
export const MAX_OPTIONS = 15;
export const predictorConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:PredictorConfig',
    type: 'object',
    properties: {
        options: { type: 'integer', minimum: MIN_OPTIONS, maximum: MAX_OPTIONS },
        threshold: { type: 'number', minimum: 0, maximum: 1 },
        excludeColumns: { type: 'array', items: { type: 'string', minLength: 1 }, uniqueItems: true },
        autoExclude: { type: 'boolean' },
        autoExcludeCorrMethod: { type: 'string', enum: ['pearson', 'kendall', 'spearman'] },
        autoExcludeCorrThreshold: { type: 'number' },
        autoExcludePpsThreshold: { type: 'number' }
    },
    required: ['labelColumn', 'options']
};
let PredictorConfig = PredictorConfig_1 = class PredictorConfig extends BaseMachineLearningConfig {
    constructor(labelColumn, options, threshold, excludeColumns, autoExclude, autoExcludeCorrMethod, autoExcludeCorrThreshold, autoExcludePpsThreshold, customFormats) {
        super(ToolType.PREDICTOR, labelColumn, customFormats);
        this.options = options;
        if (threshold !== undefined)
            this.threshold = threshold;
        if (excludeColumns !== undefined)
            this.excludeColumns = excludeColumns;
        if (autoExclude !== undefined)
            this.autoExclude = autoExclude;
        if (autoExcludeCorrMethod !== undefined)
            this.autoExcludeCorrMethod = autoExcludeCorrMethod;
        if (autoExcludeCorrThreshold !== undefined)
            this.autoExcludeCorrThreshold = autoExcludeCorrThreshold;
        if (autoExcludePpsThreshold !== undefined)
            this.autoExcludePpsThreshold = autoExcludePpsThreshold;
        JSON.validate(this, PredictorConfig_1);
    }
    get predictorColumns() {
        return ['Prediction', 'Probability'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.PREDICTOR.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.LEARN], this.ins, this.outs, ['regression', 'regressor', 'classify', 'classifier'], true);
    }
    // eslint-disable-next-line class-methods-use-this
    get hasModel() {
        return true;
    }
    getOutput(iConfigHelper, ins) {
        return this.getOutputForTrainableConfig(ins, this.in, this.out, ToolType.PREDICTOR, this.labelColumn, this.labelColumnType, this.excludeColumns, true);
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source || source.length <= 1) {
            return ToolValidationResult.getBadMetadataResult('Source is empty or has only one column.');
        }
        const columnNames = source.map((col) => col.name);
        const predictionColumnName = getRenamedColumns(columnNames, 'Prediction', ToolType.PREDICTOR);
        const probabilityColumnName = getRenamedColumns(columnNames, 'Probability', ToolType.PREDICTOR);
        const inputs = new Set([this.labelColumn]);
        const outputs = [predictionColumnName, probabilityColumnName];
        const sourceValidationResult = validateSourceWithDetail(source, inputs, outputs);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
    isValidForRun(iConfigHelper, scope, pass) {
        if (pass) {
            return scope.schemas.has(this.in) ? ToolValidationState.VALID : ToolValidationState.BAD_METADATA;
        }
        else {
            const source = scope.schemas.at(this.in);
            if (!source) {
                return ToolValidationState.BAD_METADATA;
            }
            const columnNames = source.map((col) => col.name);
            const predictionColumnName = getRenamedColumns(columnNames, 'Prediction', ToolType.PREDICTOR);
            const probabilityColumnName = getRenamedColumns(columnNames, 'Probability', ToolType.PREDICTOR);
            const outputs = [predictionColumnName, probabilityColumnName];
            return isSourceValid(source, new Set(), outputs) ? ToolValidationState.VALID : ToolValidationState.BAD_METADATA;
        }
    }
};
PredictorConfig = PredictorConfig_1 = __decorate([
    JsonSchemaV2(predictorConfigSchema, predictorConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.PREDICTOR)
], PredictorConfig);
export { PredictorConfig };
//# sourceMappingURL=predictorConfig.js.map